import { ReactNode } from "react";
import { Button } from "primereact/button";

interface FormActionsProps {
  /** Primary action button label */
  primaryLabel: string;
  /** Primary action handler */
  onPrimary: () => void;
  /** Whether primary action is loading */
  primaryLoading?: boolean;
  /** Primary action icon (optional) */
  primaryIcon?: string;
  /** Whether primary button should be type="submit" for form submission */
  primarySubmit?: boolean;
  /** Secondary action button label */
  secondaryLabel?: string;
  /** Secondary action handler */
  onSecondary?: () => void;
  /** Secondary action icon (optional) */
  secondaryIcon?: string;
  /** Additional buttons to render between secondary and primary */
  additionalActions?: ReactNode;
  /** Alignment of buttons */
  align?: "left" | "right" | "between";
  /** Additional CSS class */
  className?: string;
}

/**
 * FormActions provides consistent button layout and styling for forms.
 * Primary action on the right, secondary action on the left.
 */
export function FormActions({
  primaryLabel,
  onPrimary,
  primaryLoading = false,
  primaryIcon,
  primarySubmit = false,
  secondaryLabel,
  onSecondary,
  secondaryIcon,
  additionalActions,
  align = "right",
  className = "",
}: FormActionsProps) {
  const alignmentClasses = {
    left: "justify-content-start",
    right: "justify-content-end",
    between: "justify-content-between",
  };

  return (
    <div
      className={`flex gap-2 ${alignmentClasses[align]} ${className}`.trim()}
    >
      {secondaryLabel && onSecondary && (
        <Button
          label={secondaryLabel}
          icon={secondaryIcon}
          className="p-button-text"
          onClick={onSecondary}
          disabled={primaryLoading}
        />
      )}
      {additionalActions}
      <Button
        type={primarySubmit ? "submit" : "button"}
        label={primaryLabel}
        icon={primaryIcon}
        onClick={primarySubmit ? undefined : onPrimary}
        loading={primaryLoading}
      />
    </div>
  );
}
